import { apiPerformanceReviews } from "@/api/performance-review.api.ts";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { ReminderHistoryModal } from "@/components/reminder/ReminderHistoryModal.tsx";
import { ReminderSendModal } from "@/components/reminder/ReminderSendModal.tsx";
import { ReminderEntityType } from "@/types/reminder.types.ts";
import {
  ButtonIcon,
  Page,
  TabItem,
  Tabs,
  useModals,
  Box,
  Button,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
} from "@skailer-com/uikit";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PerformanceReviewParticipantsSummary } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewParticipantsSummary.tsx";
import { PerformanceReviewSummary } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewSummary.tsx";
import { usePerformanceReviewDetails } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiSorting, ApiSortingDirection } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import { ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import {
  ApiPerformanceReviewParticipantsSummaryFilter,
  ApiPerformanceReviewStatus,
} from "@/types/performance-review.types.ts";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { PerformanceReviewInsights } from "./components/PerformanceReviewInsights.tsx";

enum PerformanceReviewViewPageSection {
  summary = "summary",
  participants = "participants",
  insights = "insights",
}

export const PerformanceReviewViewPage = () => {
  const { id, section } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModals();
  const library = filtersLibrary();

  const {
    handleDeleteReview,
    handleReopenReview,
    handleLaunchReview,
    handlePauseReview,
    handleCompleteReview,
    canLaunchReview,
    canPauseReview,
    canCompleteReview,
    canReopenReview,
    isLaunchPending,
    isPausePending,
  } = usePerformanceReviewsHook();

  const {
    filter: { filter: participantFilter, count: participantFilterCount, typedFilters },
    filterButton: participantFilterButton,
    onApplySorting,
  } = useAppFilter<ApiPerformanceReviewParticipantsSummaryFilter>({
    filters: [
      library.page_number,
      library.text,
      library.job_profile_ids,
      library.job_role_ids,
      library.department_ids,
      library.division_ids,
      library.team_ids,
      library.location_ids,
      library.employment_type_ids,
      {
        ...library.sort,
        defaultValue: {
          fields: [{ field: EmployeeColumnId.employee_name, direction: ApiSortingDirection.asc }],
        } as ApiSorting,
      },
    ],
    source: "Performance Reviews Participants Summary",
  });

  const { data: review } = usePerformanceReviewDetails(id ? parseInt(id) : undefined, true);

  useTitle(review?.title, t("performance_review:performance_reviews"));

  const currentSection = useMemo(() => {
    if (section) {
      return section as PerformanceReviewViewPageSection;
    }
    return PerformanceReviewViewPageSection.summary;
  }, [section]);

  const navigateToSection = (section: PerformanceReviewViewPageSection) => {
    if (!review) return;
    navigate(`/performance-reviews/${review.id}/view/${section}`);
  };

  const { downloadFile } = useDownloadHelper();
  const handleExportClick = useCallback(
    (format: ApiExportFormat) => {
      if (!review) return;
      downloadFile({
        apiCall: () => apiPerformanceReviews.exportResults(review.id, { format }),
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.performanceReviews.resultsExported, {
            [analyticProperties.id]: review?.id,
            [analyticProperties.format]: format,
          });
        },
      });
    },
    [review]
  );

  useEffect(() => {
    analyticsService.trackEvent(analyticEvents.performanceReviews.viewedReview, {
      [analyticProperties.id]: id,
      [analyticProperties.tab]: currentSection,
    });
  }, [id, currentSection]);

  const activeTabIndex = useMemo(() => {
    switch (currentSection) {
      case PerformanceReviewViewPageSection.summary:
        return 0;
      case PerformanceReviewViewPageSection.participants:
        return 1;
      case PerformanceReviewViewPageSection.insights:
        return 2;
      default:
        return 0;
    }
  }, [currentSection]);

  return (
    <Page>
      <Page.Header
        title={review?.title || "..."}
        showBack={true}
        navigateTo={"/performance-reviews"}
      >
        <Stack
          direction={"horizontal"}
          gap={"sm"}
          items={"center"}
          className={"overflow-hidden print:hidden"}
        >
          {review && (
            <>
              {review.status !== ApiPerformanceReviewStatus.completed && (
                <DropdownMenu
                  trigger={
                    <ButtonIcon
                      icon={<FontAwesomeIcon icon={"fa-light fa-bell"} />}
                      variant={"secondary"}
                    ></ButtonIcon>
                  }
                >
                  <DropdownMenuItem
                    onClick={() =>
                      openModal(ReminderSendModal, {
                        recipients: [],
                        entityType: ReminderEntityType.performance_review,
                        entityIds: [review.id],
                      })
                    }
                    title={t("core:remind_everyone") + "..."}
                  />
                  <DropdownMenuItem
                    onClick={() =>
                      openModal(ReminderHistoryModal, {
                        filter: {
                          entity_type: ReminderEntityType.performance_review,
                          entity_ids: [review.id],
                        },
                      })
                    }
                    title={t("core:reminder_history")}
                  />
                </DropdownMenu>
              )}

              {currentSection == PerformanceReviewViewPageSection.participants &&
                participantFilterButton()}
              {canLaunchReview(review.status) && (
                <Button
                  isLoading={isLaunchPending}
                  onClick={() => handleLaunchReview(review)}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-play" />}
                >
                  {t("performance_review:launch_review") + "..."}
                </Button>
              )}
              {canPauseReview(review.status) && (
                <Button
                  isLoading={isPausePending}
                  onClick={() => handlePauseReview(review)}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-pause" />}
                >
                  {t("performance_review:pause_review")}
                </Button>
              )}
              {canReopenReview(review.status) && (
                <Button
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-flag-checkered" />}
                  variant={"secondary"}
                  disabled={true}
                >
                  {t("performance_review:review_completed_title")}
                </Button>
              )}
              <ButtonLink
                variant={"secondary"}
                href={`/performance-reviews/${review.id}/setup/details`}
              >
                {t("performance_review:edit_review")}
              </ButtonLink>

              <DropdownMenu className={"print:hidden"}>
                {canCompleteReview(review.status) && (
                  <DropdownMenuItem
                    onClick={() => handleCompleteReview(review)}
                    title={`${t("performance_review:complete_review")}...`}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-flag-checkered"} />}
                  />
                )}
                <DropdownMenuItem
                  onClick={() => handleExportClick(ApiExportFormat.xlsx)}
                  title={t("common:export_to_xlsx")}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-file-export"} />}
                />
                {canReopenReview(review.status) && (
                  <DropdownMenuItem
                    onClick={() => handleReopenReview(review)}
                    title={`${t("performance_review:reopen_review")}...`}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-rotate-right"} />}
                  />
                )}
                <DropdownMenuItem
                  variant={"menuDanger"}
                  onClick={() => handleDeleteReview(review)}
                  title={`${t("common:delete")}...`}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                />
              </DropdownMenu>
            </>
          )}
        </Stack>
      </Page.Header>
      <Page.Content>
        {review && (
          <Box>
            <Tabs
              className={"print:hidden"}
              onActiveTabChange={(activeTab) => {
                if (activeTab == 0) {
                  navigateToSection(PerformanceReviewViewPageSection.summary);
                } else if (activeTab == 1) {
                  navigateToSection(PerformanceReviewViewPageSection.participants);
                } else if (activeTab == 2) {
                  navigateToSection(PerformanceReviewViewPageSection.insights);
                }
              }}
              activeTab={activeTabIndex}
            >
              <TabItem title={t("common:summary")}>
                <PerformanceReviewSummary review={review} />
              </TabItem>
              <TabItem title={t("common:participation")}>
                <PerformanceReviewParticipantsSummary
                  review={review}
                  filter={participantFilter}
                  filterCount={participantFilterCount}
                  sorting={typedFilters.sorting}
                  onApplySorting={onApplySorting}
                />
              </TabItem>
              <TabItem title={t("performance_review:insights")}>
                <PerformanceReviewInsights review={review} />
              </TabItem>
            </Tabs>
          </Box>
        )}
      </Page.Content>
    </Page>
  );
};
